import { theme } from '@agentisai/design-system';
import type { CSSProperties, MouseEventHandler } from 'react';

export interface WorkstreamCardProps {
  readonly name: string;
  readonly description: string;
  readonly active?: boolean;
  readonly onClick?: MouseEventHandler<HTMLButtonElement>;
}

/**
 * Single workstream bubble in the Workstreams rail panel (S-C3).
 *
 * Lives in the app (not the design system) per frontend-architecture R0 —
 * it's a page-composition component, not a reusable DS primitive. Mirrors
 * the styling of the DS `RailPanel` Workstreams story (`accent.teal` active
 * tint), with an extra non-colour indicator (a thick left border) so the
 * active state is perceivable without colour (WCAG 1.4.1). Active card
 * carries `aria-current="true"`.
 */
export function WorkstreamCard({ name, description, active, onClick }: WorkstreamCardProps) {
  const borderColor = active ? theme.color.accent.teal[500] : theme.color.neutral.border;
  // Non-colour active indicator: a 3px left border vs. 1px elsewhere makes the
  // active state visually distinct in a black-and-white render too (WCAG 1.4.1).
  // Inset the padding so the text doesn't shift between active / inactive.
  const style: CSSProperties = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    marginBottom: theme.space[2],
    padding: theme.space[3],
    paddingLeft: active ? `calc(${theme.space[3]} - 2px)` : theme.space[3],
    background: active ? theme.color.accent.teal[50] : theme.color.neutral.white,
    border: `1px solid ${borderColor}`,
    borderLeftWidth: active ? '3px' : '1px',
    borderRadius: theme.radii.lg,
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily.sans,
  };
  return (
    <button
      type="button"
      aria-current={active ? 'true' : undefined}
      onClick={onClick}
      style={style}
    >
      <div
        style={{
          fontSize: theme.typography.fontSize.bodySm,
          fontWeight: theme.typography.fontWeight.semibold,
          color: theme.color.neutral.body,
          marginBottom: theme.space[1],
        }}
      >
        {name}
      </div>
      <div
        style={{
          fontSize: theme.typography.fontSize.tiny,
          fontWeight: theme.typography.fontWeight.regular,
          color: theme.color.neutral.faint,
          lineHeight: theme.typography.lineHeight.body,
        }}
      >
        {description}
      </div>
    </button>
  );
}
WorkstreamCard.displayName = 'WorkstreamCard';
